import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
  ResponseMeta,
} from '@/components/DataTable';
import {
  listPersonalRunningNowDataTable,
  getPersonalRunningNowFilterOptions,
} from '@kirin/api-client/api/shadow-ai';
import type {
  PersonalRunningNowListItem,
  PersonalRunningNowFilterOptionField,
} from '@kirin/api-client/contracts/account-shadow-ai-running-now';
import type { RunningNowSortField } from '@kirin/api-client/contracts/shadow-ai-running-now';
import type { ToolApprovalFilter } from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { apiClient } from '@/lib/api-client';

/**
 * Personal running-now table meta: the shared `meta` plus the heartbeat that
 * drives AgentStatus through the single `onMeta` egress.
 */
export type PersonalRunningNowMeta = ResponseMeta & {
  lastHeartbeat: string | null;
};

export async function fetchPersonalRunningNow(
  params: DataFetchParams,
  signal?: AbortSignal,
  isRoot?: true,
  approval?: ToolApprovalFilter
): Promise<
  DataFetchResponse<PersonalRunningNowListItem, PersonalRunningNowMeta>
> {
  const sortField = params.sort?.field as RunningNowSortField | undefined;
  const response = await listPersonalRunningNowDataTable(
    apiClient,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      search: params.search,
      isRoot,
      approval,
    },
    { signal }
  );

  const meta: PersonalRunningNowMeta = {
    activeFilters: response.meta.activeFilters,
    search: response.meta.search,
    lastHeartbeat: response.lastHeartbeat,
  };

  return {
    data: response.data,
    pagination: response.pagination,
    meta,
  };
}

export async function fetchPersonalRunningNowFilterOptions(
  field: PersonalRunningNowFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getPersonalRunningNowFilterOptions(apiClient, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  const options = response.options.map(value => ({ value, label: value }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
